/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import Dialog from '@comp/Dialog/Dialog';
import Carousel from '@comp/Carousel/Carousel';
import BatchCardNew from '@comp/BatchCard/BatchCardNew';
import { enrollInBatch } from '@actions/batches';
import {
  BATCH_UNENROLLED,
  BATCH_ENROLLED,
  BATCH_KEYS,
  SYLLABUS_SEE_ALL_CLICKED
} from '@constants/segment';
import { evtNameCategoryMap, getCourseEnrollMessage } from '@constants/index';
import { segment } from '@actions/analytics';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';

const BatchFeedBlock = ({
  feedBlock,
  goalSlug,
  goalUID,
  dataAnalytics,
  lastPrimarySource
}) => {
  const dispatch = useDispatchToStore();
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const selectedGoalUID = useSelectedGoalUID();
  const count = feedBlock?.extraBlockinfo?.count;
  const seeAllLink = count > 3 ? `/goal/${goalSlug}/${goalUID}/batches` : null;
  const href = '/goal/[goalSlug]/[goalUID]/batches';
  const { label } = feedBlock;
  const viewRef = useRef();
  const batchRef = useRef('');
  const segmentDataRef = useRef({});
  const { open: showSnackbar } = useSnackbar();

  /* batch feed block is using its own toggle enroll,
      not from the home page enroll function */
  const toggleEnroll = useCallback(
    (batchData) =>
      async (batchUid, isEnrolled, batch, segmentData, isRecordedGoal) => {
        if (isEnrolled) {
          segmentDataRef.current = {
            ...segmentData,
            batch_state: batch.state,
            start_date: batch.startsAt,
            end_date: batch.completedAt
          };
          batchRef.current = batchUid;
          setUnEnrollModalOpen(true);
        } else {
          await dispatch(enrollInBatch(batchUid, !isEnrolled)).then((res) => {
            if (res.result)
              segment.track(
                BATCH_ENROLLED,
                segmentData,
                evtNameCategoryMap.WITHOUT_CATEGORY,
                true,
                BATCH_KEYS
              );
          });
          const { uid, name } = batchData?.goal || {};
          showSnackbar({
            message: getCourseEnrollMessage({
              type: 'Courses',
              goalUID: uid,
              selectedGoalUID,
              goalName: name,
              isRecordedGoal
            }),
            type: 'success'
          });
        }
      },
    [dispatch, lastPrimarySource]
  );

  const acceptOnClick = () => {
    dispatch(enrollInBatch(batchRef.current, false)).then((res) => {
      if (res.result)
        segment.track(
          BATCH_UNENROLLED,
          segmentDataRef.current,
          evtNameCategoryMap.WITHOUT_CATEGORY,
          true,
          BATCH_KEYS
        );
      segmentDataRef.current = {};
    });
    setUnEnrollModalOpen(false);
  };

  const onCloseModal = () => setUnEnrollModalOpen(false);

  const seeAllClickEvent = () => {
    const goal = feedBlock.data?.[0]?.goal;
    segment.track(SYLLABUS_SEE_ALL_CLICKED, {
      expansion_type: feedBlock.label,
      goal_name: goal.name,
      goal_uid: goal.uid,
      is_subscription_active: hasActiveSubscriptionToGoal(goal, goal.uid),
      subscription_duration: 'NA',
      subscription_end_date: 'NA',
      last_primary_source: lastPrimarySource,
      last_primary_source_section: feedBlock.label
    });
  };

  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="All courses from this batch will be removed from your schedule and you will stop receiving updates related to this batch"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Carousel
        label={label}
        seeAllLink={seeAllLink}
        href={href}
        dataAnalytics="goal-batches"
        handleClickSeeAll={seeAllClickEvent}
        ref={viewRef}
      >
        {feedBlock.batches.map((batchData, _index) => (
          <BatchCardNew
            label={label}
            index={_index}
            key={batchData.uid}
            batch={batchData}
            hideEnroll
            toggleEnroll={toggleEnroll(batchData)}
            lastPrimarySource={lastPrimarySource}
            dataAnalytics={dataAnalytics}
          />
        ))}
      </Carousel>
    </>
  );
};

export default BatchFeedBlock;

BatchFeedBlock.propTypes = {
  feedBlock: PropTypes.objectOf(PropTypes.any).isRequired,
  goalSlug: PropTypes.string.isRequired,
  goalUID: PropTypes.string.isRequired,
  dataAnalytics: PropTypes.string,
  lastPrimarySource: PropTypes.string
};

BatchFeedBlock.defaultProps = {
  dataAnalytics: '',
  lastPrimarySource: ''
};
